// A leitura do Espelho sobre a noite — o bloco que sobe para o topo.
//
// ═════════════════════════════════════════════════════════════════════════
// 🔴 DUAS COISAS CHAMADAS "MIRROR" VIVIAM NESTA TELA, E SÓ UMA ERA REAL
// ═════════════════════════════════════════════════════════════════════════
// (1) No RODAPÉ havia um bloco "Mirror Insight" com uma frase FIXA no locale
//     ("You slept 45min less than your optimal 8h this week…") — e ele estava
//     desligado desde a auditoria de 23/04, atrás de um `{false && …}`, com o
//     bilhete "fake Mirror Insight — re-wire with real Padrão B". Promovê-lo
//     ao topo teria colocado uma frase inventada na primeira dobra da tela,
//     que é o oposto exato do que este lote existe para consertar. Ele foi
//     REMOVIDO, não promovido.
//
// (2) A leitura REAL — a edge function `sleep-react`, via useSleepReact — já
//     rodava e já aparecia, mas ANINHADA DENTRO do card do score, abaixo do
//     anel do 92. Ou seja: a vantagem competitiva do produto renderizava como
//     subordinada da nota do dia. É esta que sobe.
//
// A Oura de 2026 usa IA generativa para resumo narrativo com ajuste
// específico. O VYV já faz isso — e escondia embaixo de um número.
//
// ⚠️ SILENCIOSO NO ERRO, de propósito e como antes: sem reação, o card
// simplesmente não renderiza. Estado vazio falso é pior que ausência.

import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Volume2, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useVoice } from "@/hooks/useVoice";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";

const ACCENT = "#00e5ff";

interface MirrorSleepCardProps {
  /** Texto vindo de useSleepReact — a edge function sleep-react. */
  reaction: string | null;
  isLoading: boolean;
  error: string | null;
  /** sleep_logs.id — identidade da denúncia (a reação não tem id próprio). */
  logId: string | null;
}

const MirrorSleepCard: React.FC<MirrorSleepCardProps> = ({ reaction, isLoading, error, logId }) => {
  const { t } = useLanguage();
  const voice = useVoice();
  const { isReported } = useReportedContent();

  if (error) return null;
  if (!isLoading && !reaction) return null;
  if (isReported(REPORT_SURFACES.REACTION_SLEEP, logId ?? undefined)) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl text-left mb-5 relative overflow-hidden"
      style={{
        background: "hsl(220 50% 12% / 0.55)",
        border: `1px solid ${ACCENT}40`,
        boxShadow: `0 0 24px ${ACCENT}12`,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        padding: 16,
      }}
    >
      {/* Borda superior animada — a mesma assinatura visual que o bloco morto
          do rodapé usava. O tratamento era bom; o conteúdo é que era falso. */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[1px]"
        animate={{ backgroundPosition: ["0% 50%", "200% 50%"] }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        style={{
          background: `linear-gradient(90deg, transparent, ${ACCENT}60, hsl(265 60% 70% / 0.45), ${ACCENT}60, transparent)`,
          backgroundSize: "200% 100%",
        }}
      />

      <div className="flex items-center gap-2 mb-2">
        <motion.div
          className="w-7 h-7 rounded-xl flex items-center justify-center shrink-0"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          style={{ background: `${ACCENT}1f`, border: `1px solid ${ACCENT}38` }}
        >
          <Sparkles size={13} style={{ color: ACCENT }} />
        </motion.div>
        <p
          className="font-display uppercase"
          style={{ fontSize: 9, letterSpacing: "0.22em", color: ACCENT, opacity: 0.85 }}
        >
          {t("sleep.mirrorInsight")}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <div className="h-3 w-full rounded animate-pulse" style={{ background: "hsl(218 14% 22%)" }} />
          <div className="h-3 w-5/6 rounded animate-pulse" style={{ background: "hsl(218 14% 20%)" }} />
          <div className="h-3 w-3/5 rounded animate-pulse" style={{ background: "hsl(218 14% 18%)" }} />
        </div>
      ) : reaction ? (
        <>
          <p className="font-body text-[13px] text-white leading-relaxed">{reaction}</p>
          <div className="flex justify-end items-center gap-1 mt-2">
            {logId && (
              <ReportContentButton
                surface={REPORT_SURFACES.REACTION_SLEEP}
                contentId={logId}
                snapshot={reaction}
                context={{ module: "sleep" }}
              />
            )}
            <button
              onClick={() => {
                if (voice.isPlaying) {
                  voice.stop();
                  return;
                }
                // Decisão de arquitetura (Rafa 25/04): sem fallback de Web
                // Speech — a falha fica silenciosa para o usuário e visível no
                // console.
                voice.speak(reaction).catch((err) => {
                  console.error("[MirrorSleepCard] voice.speak failed", err);
                });
              }}
              disabled={voice.isLoading}
              className={`w-6 h-6 rounded-full flex items-center justify-center active:scale-90 disabled:opacity-70 ${voice.isPlaying ? "animate-pulse" : ""}`}
              style={{ background: `${ACCENT}26`, border: `1px solid ${ACCENT}40` }}
              aria-label={t("common.listen")}
            >
              {voice.isLoading ? (
                <Loader2 size={14} className="animate-spin" style={{ color: ACCENT }} />
              ) : (
                <Volume2 size={14} style={{ color: ACCENT }} />
              )}
            </button>
          </div>
        </>
      ) : null}
    </motion.div>
  );
};

export default MirrorSleepCard;
